<template>
	<leaflet-layer-group ref="layerGroup">
		<leaflet-marker
			v-for="(marker, index) of selectedTripMarkers"
			:ref="marker.id"
			:key="marker.id"
			:latitude="marker.latitude"
			:longitude="marker.longitude"
			:icon="marker.icon"
			:iconColor="marker.iconColor"
			preventRefreshMarkersOnMoveMap
			:bringToFront="selectedTrip ? selectedTrip.id === marker.id : false"
			:isSelected="selectedTrip ? selectedTrip.id === marker.id : false"
			@click="onMarkerClicked($event, index)"
		/>
	</leaflet-layer-group>
</template>

<script>
import { statuses } from '@/core';
import { mapDynamicGetters, mapDynamicActions } from '@/core/mapDynamic';
import createIconMixin from '@/components/map/CreateIcon.mixin.js';
import { addAsDroppable, removeAsDroppable } from '@/DragDrop.js';

export default {
	name: 'MapTrips',
	inject: [ 'state', 'namespace' ],
	mixins: [createIconMixin],
	props: {
		tripDetailView: { type: Boolean }
	},
	data() {
		return {
			ready: false,
			nodes: [],
			createdMarkers: [],
			selectedMarkers:  [],
			icon: null,
			liveIcon: null,
		};
	},
	computed: {
		computedStoreNamespace() {
			return `loadPlanning/${this.namespace.id}`;
		},
		...mapDynamicGetters('computedStoreNamespace', [ 'trips', 'tripDetails', 'tripsLoading' ]),
		validTrips() {
			return this.trips.filter(x => this.hasLatLng(x.fromAddress)).map(y => ({ latitude: y.fromAddress.latitude, longitude: y.fromAddress.longitude, ...y }));
		},
		selectedTrip() {
			return this.tripDetails;
		},
		selectedTripMarkers() {
			if (!this.tripDetailView || !this.selectedTrip) return this.createdMarkers;
			return this.createdMarkers.filter( x => x.id === this.selectedTrip.id );
		}
	},
	watch: {
		trips: {
			immediate: true,
			handler(newVal, oldVal) {
				if (!this.ready || this.tripsLoading || this.tripDetailView) return;
				this.setupDefaultPoints();
				if ( (!oldVal && newVal && newVal.length === 0) ||
					(oldVal && newVal && oldVal.length === 0 && newVal.length === 0) ) return;
				this.createNodes();
			}
		},
		tripDetails: {
			immediate: true,
			handler(newVal, oldVal) {
				if (!this.ready || this.tripsLoading || this.tripDetailView) return;
				if ( (!oldVal && !newVal) || !newVal ) {
					this.resetSelectedMarkers();
					return;
				}
				if (oldVal && oldVal.id === newVal.id) return;
				this.resetSelectedMarkers();
				this.selectedMarkers.push(newVal.id);
				if (this.$refs[newVal.id] && this.$refs[newVal.id][0]) this.$refs[newVal.id][0].setSelected(true);
			}
		}
	},
	beforeDestroy() {
		this.state.map.off('click', this.mapClick);
	},
	async mounted() {
		await this.$nextTick();
		this.icon = this.createIcon({
			iconName: 'truck-front',
			iconType: 'tim',
		});
		this.liveIcon = this.createIcon({
			iconName: 'truck-front',
			iconType: 'tim',
			shape: 'marker',
			anchor: [ 15, 35 ],
		});
		this.state.map.on('click', this.mapClick);

		this.setupDefaultPoints();
		this.createNodes();
		this.ready = true;
	},
	methods: {
		...mapDynamicActions('computedStoreNamespace',['setTripDetails']),
		setupDefaultPoints() {
			let points = {
				type: 'FeatureCollection',
				features: []
			};

			this.validTrips.forEach(item => {
				points.features.push({
					type: 'Feature',
					id: item.id,
					geometry: {
						type: 'Point',
						coordinates: [ item.latitude, item.longitude ]
					},
					properties: { id: item.id, currentPosition: item.currentPosition, status: item.status }
				});
			});

			this.nodes = Object.freeze(points.features);
		},
		async createNodes() {
			if (this.createdMarkers && this.createdMarkers.length > 0) {
				this.createdMarkers.forEach(marker => {
					removeAsDroppable(`mapTrips${marker.id}`);
				});
			}
			let createdMarkers = [];
			for (let { latitude, longitude, ...node } of
				this.nodes = Object.freeze(this.nodes.map(({ geometry: { coordinates }, properties }) => ({ latitude: coordinates[0], longitude: coordinates[1], ...properties })))
			) {
				let coordinates = this.isLive(node.currentPosition) ? node.currentPosition.coords : { latitude,longitude };
				createdMarkers.push({
					id: node.id,
					coordinates: [ coordinates.latitude, coordinates.longitude ],
					latitude: coordinates.latitude,
					longitude: coordinates.longitude,
					tripId: node.id,
					icon: this.getIcon(node),
					iconColor: statuses.color(node.status),
					selected: false,
					clickTimeout: null,
					context: this,
				});
			}
			this.createdMarkers = Object.freeze(createdMarkers);
			await this.$nextTick();

			this.createdMarkers.forEach(marker => {
				if (this.$refs[marker.id][0]) {
					let el = this.$refs[marker.id][0].getHtml();
					addAsDroppable(el, `mapTrips${marker.id}`, (payload) => this.handleDrop(marker.id, payload) );
				}
			});

			this.resetSelectedMarkers();
		},
		handleDrop(tripId, orders) {
			let trip = this.trips.find(x => x.id === tripId);
			if (!Array.isArray(orders)) {
				orders = [orders];
			}
			this.$emit('showAddToTrip', { trip: trip, orders: orders });
		},
		resetSelectedMarkers() {
			if (this.selectedMarkers.length > 0) {
				for (let selectedIndex of this.selectedMarkers) {
					if (this.$refs[selectedIndex][0]) this.$refs[selectedIndex][0].setSelected(false);
				}
				this.selectedMarkers = [];
			}
		},
		getIcon(node) {
			if (this.isLive(node.currentPosition)) return this.liveIcon;
			return this.icon;
		},
		isLive(currentPosition) {
			return currentPosition && currentPosition.coords && this.hasLatLng(currentPosition.coords);
		},
		hasLatLng(item) {
			return item && item.latitude && item.longitude;
		},
		mapClick() {
			if (!this.tripDetailView) this.setTripDetails(null);
		},
		onMarkerClicked(event, markerIndex) {
			let markerData = this.createdMarkers[markerIndex];
			if (!markerData) return;

			this.resetSelectedMarkers();
			if (this.tripDetails && markerData.tripId === this.tripDetails.id) {
				this.setTripDetails(null);
			} else {
				this.setTripDetails(markerData.tripId);
			}
		},
	}
};
</script>

<style lang="scss" scoped>

</style>